import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import MaskedInput from 'react-text-mask'

import {
  COUNTRY_OPTIONS,
  getRegionsForCountry,
  getRegionLabelForCountry
} from '../constants/customFieldValuesConstants';
import { getExtendedPostalCodeMask, getPostalCodePipe, isValidPostalCode } from '../../../shared/postalCodeUtils';
import { addressHasAnyValues } from '../actions/customFieldValuesActionCreators';

const AddressCustomFieldValue = ({
  team,
  csrfToken,
  confirm,
  customFieldValue,
  customFieldValueChanged,
  highlightInvalidInputs=false,
  defaultCountry
}) => {
  const country = customFieldValue.value.country || defaultCountry || "US";
  const regionOptions = getRegionsForCountry(country);
  const regionLabel = getRegionLabelForCountry(country);
  const highlightMissing = highlightInvalidInputs && (
    customFieldValue.custom_field.required || addressHasAnyValues(customFieldValue.value)
  );
  const highlightClass = (isFilled) => (highlightMissing && !isFilled ? " highlight" : "");

  // Initialize the country in the value if not already set
  useEffect(() => {
    if (!customFieldValue.value.country && defaultCountry) {
      var value = Object.assign({}, customFieldValue.value, { country: defaultCountry });
      var updated = Object.assign({}, customFieldValue, { value: value });
      customFieldValueChanged(updated);
    }
  }, []);

  return (
    <div className="row mt-3" style={{"padding": "10px 0 15px 0"}}>
      <div className="col-12">
        <div className="row">
          <div className="col-12">
            <p style={{
                 "fontSize": "16px",
                 "marginBottom": "12px"
               }}>
              <strong>{customFieldValue.custom_field.name}</strong>{!customFieldValue.custom_field.required ? " (optional)" : null}
            </p>
          </div>
        </div>
        <div className="form-row mb-3">
          <div className="col-6">
            <select className="form-control"
                    autoComplete="country"
                    style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)", "fontSize": "14px"}}
                    onChange={
                      (e) => {
                        var value = Object.assign({}, customFieldValue.value, {
                          country: e.target.value,
                          state: "",
                          zip: ""
                        });
                        var updated = Object.assign({}, customFieldValue, {value: value});

                        customFieldValueChanged(updated);
                      }
                    }
                    value={country}>
              {COUNTRY_OPTIONS.map((countryOption, index) =>
                <option key={index} value={countryOption.code}>
                  {countryOption.name}
                </option>
              )}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="col-6">
            <input type="text"
                   placeholder="Address 1"
                   autoComplete="address-line1"
                   style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)", "fontSize": "14px"}}
                   value={customFieldValue.value.address1 || ""}
                   onChange={
                     (e) => {
                       var value = Object.assign({}, customFieldValue.value, {address1: e.target.value});
                       var updated = Object.assign({}, customFieldValue, {value: value});

                       customFieldValueChanged(updated);
                     }
                   }
                   className={"form-control" + highlightClass(customFieldValue.value.address1)} />
          </div>
          <div className="col-6">
            <input type="text"
                   placeholder="Address 2"
                   autoComplete="address-line2"
                   style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)", "fontSize": "14px"}}
                   value={customFieldValue.value.address2 || ""}
                   onChange={
                     (e) => {
                       var value = Object.assign({}, customFieldValue.value, {address2: e.target.value});
                       var updated = Object.assign({}, customFieldValue, {value: value});

                       customFieldValueChanged(updated);
                     }
                   }
                   className="form-control" />
          </div>
        </div>
        <div className="form-row mt-3">
          <div className="col-6">
            <input type="text"
                   placeholder="City"
                   autoComplete="address-level2"
                   style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)", "fontSize": "14px"}}
                   value={customFieldValue.value.city || ""}
                   onChange={
                     (e) => {
                       var value = Object.assign({}, customFieldValue.value, {city: e.target.value});
                       var updated = Object.assign({}, customFieldValue, {value: value});

                       customFieldValueChanged(updated);
                     }
                   }
                   className={"form-control" + highlightClass(customFieldValue.value.city)} />
          </div>
          <div className="col-3">
            <select className={"form-control" + highlightClass(customFieldValue.value.state)}
                    autoComplete="address-level1"
                    style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)", "fontSize": "14px"}}
                    onChange={
                      (e) => {
                        var value = Object.assign({}, customFieldValue.value, {state: e.target.value});
                        var updated = Object.assign({}, customFieldValue, {value: value});

                        customFieldValueChanged(updated);
                      }
                    }
                    value={customFieldValue.value.state || ""}>
              <option value="">{regionLabel}</option>
              {regionOptions.map((region, index) =>
                <option key={index} value={region.code}>
                  {region.name}
                </option>
              )}
            </select>
          </div>
          <div className="col-3">
            <MaskedInput className={"form-control" + highlightClass(customFieldValue.value.zip && isValidPostalCode(customFieldValue.value.zip, country))}
                         autoComplete="postal-code"
                         style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)", "fontSize": "14px"}}
                         placeholder="Postal code"
                         guide={false}
                         onChange={
                           (e) => {
                             var value = Object.assign({}, customFieldValue.value, {zip: e.target.value});
                             var updated = Object.assign({}, customFieldValue, {value: value});

                             customFieldValueChanged(updated);
                           }
                         }
                         value={customFieldValue.value.zip || ""}
                         pipe={getPostalCodePipe(country)}
                         mask={(value) => getExtendedPostalCodeMask(value, country)} />
          </div>
        </div>
      </div>
    </div>
  );
};

AddressCustomFieldValue.propTypes = {
  team: PropTypes.object,
  csrfToken: PropTypes.string,
  confirm: PropTypes.object,
  customFieldValue: PropTypes.object.isRequired,
  customFieldValueChanged: PropTypes.func.isRequired,
  highlightInvalidInputs: PropTypes.bool,
  defaultCountry: PropTypes.string
};

export default AddressCustomFieldValue;
